import { cloneDeep } from "lodash";

import {
  authStateOption,
  defaultProtocolType,
  deviceStatusOption,
  surveillanceDeviceTypeOption,
  surveillanceProductCategoryOption,
} from "./const";

const normalizeIdentity = (value) => String(value || "").toLowerCase();

export const getSurveillanceDeviceType = (productCategory) => {
  const normalizedCategory = String(productCategory || "").trim().toLowerCase();

  if (normalizedCategory === surveillanceProductCategoryOption.nvr.toLowerCase()) {
    return surveillanceDeviceTypeOption.nvr;
  }

  if (normalizedCategory === surveillanceProductCategoryOption.ipcam.toLowerCase()) {
    return surveillanceDeviceTypeOption.ipcam;
  }

  return surveillanceDeviceTypeOption.unknown;
};

export const handleSurveillanceSitesResponse = (wsData) => {
  const data = (
    wsData?.response?.data?.sites ||
    wsData?.result?.data?.sites ||
    wsData?.result?.batchData ||
    wsData?.response?.data?.batchData ||
    wsData?.result?.data ||
    wsData?.response?.data ||
    wsData?.data ||
    []
  );
  const sites = Array.isArray(data) ? data : data?.sites || [];

  if (!Array.isArray(sites)) {
    return [];
  }

  return sites.map((site) => {
    const info = site.info || site;

    return {
      id: info.siteId || info.id || info.uuid,
      name: info.siteName || info.name,
    };
  }).filter((site) => site.id);
};

export const handleSurveillanceExistingDevicesResponse = ({ data }) => {
  return (data || []).map((item) => {
    const info = item.info || item;

    return {
      deviceId: info.deviceId || info.macAddress || item.deviceId || item.macAddress || "",
    };
  }).filter((device) => device.deviceId);
};

export const handleSurveillanceInventoryDevicesResponse = ({ data }) => {
  return (data || []).map((item) => {
    const info = item.info || item;

    return {
      deviceId: info.deviceId || info.macAddress || item.deviceId || item.macAddress || "",
    };
  }).filter((device) => device.deviceId);
};

export const handleSurveillanceDiscoveryResponse = ({ data, devices, existedDevices, inventoryDevices }) => {
  const updatedDevices = [];
  const existingIds = new Set([
    ...(existedDevices || []).map((device) => normalizeIdentity(device.deviceId)),
    ...(inventoryDevices || []).map((device) => normalizeIdentity(device.deviceId)),
  ]);
  const currentIds = new Set((devices || []).map((device) => normalizeIdentity(device.deviceId)));

  for (let i = 0; i < (data || []).length; i++) {
    const item = data[i];
    const info = item.info || item;

    // The discovery API reports the device category in productCategory.
    const productCategory = info.productCategory;
    const deviceId = info.deviceId || info.macAddress || "";
    const normalizedId = normalizeIdentity(deviceId);

    if (!normalizedId || existingIds.has(normalizedId) || currentIds.has(normalizedId)) {
      continue;
    }

    const deviceType = getSurveillanceDeviceType(productCategory);
    if (deviceType.value === surveillanceDeviceTypeOption.unknown.value) {
      continue;
    }

    const authStatus = info.authStatus === authStateOption.passed ? authStateOption.passed : authStateOption.failed;
    const status = deviceStatusOption.unmanaged;
    const username = info.username || info.userName || info.account || info.accountName || "";
    const password = info.password || info.passwd || info.accountPassword || "";

    updatedDevices.push({
      checked: false,
      prevChecked: false,
      status,
      authStatus,
      isAuthenticatedByDiscovery: authStatus === authStateOption.passed,
      deviceType,
      deviceId,
      modelName: info.modelName || "",
      productName: info.productName || "",
      brandName: info.brandName || "",
      ipVersion: info.ipVersion || 4,
      ipAddress: info.ipAddress || "",
      webPortNumber: info.webPortNumber || "",
      subnetMask: info.subnetMask || "",
      defaultGateway: info.defaultGateway || "",
      primaryDNS: info.primaryDNS || "",
      secondaryDNS: info.secondaryDNS || "",
      dhcpEnable: info.dhcpEnable ?? null,
      fwVersion: info.version || "",
      version: info.version || "",
      hardwareRevision: info.hardwareRevision || info.hardwareVersion || "",
      deviceIPType: info.deviceIPType ?? null,
      managedByNMS: info.managedByNMS ?? null,
      nmsURL: info.nmsURL || "",
      mgmtStatus: info.mgmtStatus ?? null,
      mgmtMode: info.mgmtMode || "",
      productCategory,
      protocolType: info.protocolType || defaultProtocolType,
      username,
      password,
      provisioning: false,
      ref: {
        ...info,
        status,
      },
    });

    currentIds.add(normalizedId);
  }

  return updatedDevices;
};

export const handleSurveillanceAuthenticationResponse = ({ data, readyDevices, username, password }) => {
  const updatedReadyDevices = cloneDeep(readyDevices);
  const updatedResults = [];

  for (let i = 0; i < (data || []).length; i++) {
    const result = data[i];
    const deviceId = result.deviceId || result.macAddress;
    const index = updatedReadyDevices.findIndex((device) => normalizeIdentity(device.deviceId) === normalizeIdentity(deviceId));
    const isAuthenticated = result.status === "success" || result.code === 0;

    updatedResults.push({
      deviceId,
      isAuthenticated,
      message: result.message || result.status || (isAuthenticated ? "Success" : "Failure"),
    });

    if (index !== -1) {
      updatedReadyDevices[index].authStatus = isAuthenticated ? authStateOption.passed : authStateOption.failed;
      updatedReadyDevices[index].isAuthenticatedByDiscovery = false;

      if (isAuthenticated) {
        updatedReadyDevices[index].username = username;
        updatedReadyDevices[index].password = password;
        updatedReadyDevices[index].deviceAccount = { username, password };
      }
    }
  }

  return {
    updatedReadyDevices,
    updatedResults,
  };
};

export const handleSurveillanceAdoptionResponse = ({ data, readyDevices }) => {
  const updatedReadyDevices = cloneDeep(readyDevices);
  const updatedResults = [];

  for (let i = 0; i < (data || []).length; i++) {
    const result = data[i];
    const deviceId = result.deviceId || result.macAddress;
    const isAdopted = result.code === 0;

    updatedResults.push({
      deviceId,
      isAdopted,
      message: result.message || (isAdopted ? "Success" : "Failure"),
      code: result.code,
    });

    const index = updatedReadyDevices.findIndex((device) => normalizeIdentity(device.deviceId) === normalizeIdentity(deviceId));
    if (index !== -1) {
      updatedReadyDevices[index].provisioning = isAdopted;
      updatedReadyDevices[index].status = isAdopted
        ? deviceStatusOption.provisioning
        : updatedReadyDevices[index].status;
      updatedReadyDevices[index].adoptedResult = result;
    }
  }

  return {
    updatedReadyDevices,
    updatedResults,
  };
};

export const handleSurveillanceUnexpectedResponse = ({ socketIdType, results, readyDevices }) => {
  const updatedResults = cloneDeep(results || []);

  for (let i = 0; i < (readyDevices || []).length; i++) {
    const device = readyDevices[i];
    const existingResult = updatedResults.find((result) => normalizeIdentity(result.deviceId) === normalizeIdentity(device.deviceId));

    if (existingResult) {
      continue;
    }

    if (socketIdType === "auth") {
      updatedResults.push({
        deviceId: device.deviceId,
        isAuthenticated: false,
        message: "Timeout",
      });
    }

    if (socketIdType === "adopt") {
      updatedResults.push({
        deviceId: device.deviceId,
        isAdopted: false,
        message: "Timeout",
        code: 4,
      });
    }
  }

  return updatedResults;
};
